import type { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import type { FamilyMemberDetections } from 'src/data-capture/hooks/file-uploads/useFamilyFileDetections';
import type { MeetingScope } from 'src/meetings/scope';

// A recording's file is owned by a single client, so only the family endpoints can read and
// finalise the detections of the other clients in the meeting. Showing the suggestions and
// finalising a review must use the same family, or a finalised review never clears its suggestion.
// A family member who didn't attend can still have detections on the recording, so with
// `reviewsWholeFamily` a lone attendee's family is read too.
export function resolveRecordingFamilyId(
  scope: MeetingScope,
  clientParticipantCount: number,
  leadClientFamilyId: string | undefined,
  reviewsWholeFamily = false,
): string | undefined {
  if (scope.kind === 'family') return scope.familyId;
  return clientParticipantCount > 1 || reviewsWholeFamily ? leadClientFamilyId : undefined;
}

type ProfileUpdateTarget = { id: string; firstName: string; name: string };

// Attendees keep their meeting details; a family member who didn't attend is named from their
// detections, so their data can still be reviewed.
export function profileUpdateTargets(
  clientIds: string[],
  participants: ProfileUpdateTarget[],
  familyDetectionMembers: FamilyMemberDetections[],
): ProfileUpdateTarget[] {
  const participantById = new Map(participants.map(participant => [participant.id, participant]));
  const memberById = new Map(
    familyDetectionMembers.map(member => [member.client.id, member.client]),
  );
  return clientIds.flatMap(id => {
    const participant = participantById.get(id);
    if (participant) return [participant];
    const member = memberById.get(id);
    if (!member) return [];
    return [
      {
        id,
        firstName: member.firstName,
        name: `${member.firstName} ${member.lastName}`.trim(),
      },
    ];
  });
}

// Resolving the file is how every review entry point outside the family flow marks it done, so
// a resolved file has nothing left to review, whatever its per-client detection rows say.
export function clientsAwaitingProfileUpdate({
  recordingFamilyId,
  recordingFile,
  familyDetectionMembers,
}: {
  recordingFamilyId: string | undefined;
  recordingFile: UploadedFile | undefined;
  familyDetectionMembers: FamilyMemberDetections[];
}): string[] {
  if (!recordingFile || recordingFile.isResolved) return [];
  if (recordingFamilyId) {
    return familyDetectionMembers
      .filter(member => member.detections.length > 0 && !member.isProcessed)
      .map(member => member.client.id);
  }
  // The client file endpoints only serve the file to the client who owns it.
  return recordingFile.detectedClients
    .filter(detected => detected.id === recordingFile.clientId && !detected.isProcessed)
    .map(detected => detected.id);
}
